import Api from "../../../services/api";
import { getDriverChangeOfUserAssignedToDevice } from "../../../sync/driverChangeHelper";
import { IDriverChange, TDriver } from "../../../model/DriverChangeLog";
import { dispatcher, DispatcherEvents } from "../../../utils/dispatcher";
import DutyStatusLog from "../../../model/DutyStatusLog";
import VehicleService from "../../../services/vehicle";
import { DutyStatusLogService } from "../../../model/DutyStatusLogs";
import { UserService } from "../../../utils/users";
import { DriveStore } from "../../../store/reducers";
import { BaseCache } from "../../../utils/storage/cache";
import { Device } from "../../../model/Device";
import { OpenCabWorkflowActions } from "../../../store/openCabWorkflow/actions";
import { getCurrentVehicle, isNoVehicle } from "../../../utils/device";
import { getServerDate } from "../../../utils/storage/date";

/**
 * Minimal interface for Context API - only the methods we need for co-driver access checks
 */
export interface IContextApi {
    call: Api["call"];
}

/**
 * Minimal interface for Context - only the properties we need
 */
export interface IContextWithApi {
    api: IContextApi;
}

/**
 * Minimal interface for AppManager - only the methods we need
 */
export interface IAppManagerWithContext {
    getCachedContext: (userName: string) => IContextWithApi | undefined;
}

/**
 * Get the latest driver change for a device
 */
export function getLatestDriverChange(
    api: Api,
    deviceId: string
): Promise<IDriverChange | null> {
    return getDriverChangeOfUserAssignedToDevice(api, deviceId)
        .then(driverChanges => {
            return driverChanges.length > 0 ? driverChanges[0] : null;
        });
}

/**
 * Get the driver from device status info (fallback when no driver change exists)
 */
export function getDriverFromDeviceStatusInfo(
    api: Api,
    deviceId: string
): Promise<TDriver | undefined> {
    return new Promise<TDriver | undefined>((resolve, reject) => {
        api.call(
            "Get",
            {
                typeName: "DeviceStatusInfo",
                search: {
                    deviceSearch: { id: deviceId }
                },
                propertySelector: {
                    fields: ["driver"],
                    isIncluded: true
                }
            },
            statusInfo => {
                resolve(statusInfo && statusInfo[0] ? statusInfo[0].driver : undefined);
            },
            reject
        );
    });
}

/**
 * Get detailed user info for a driver
 * @param api - API instance
 * @param driverId - Driver ID to look up
 * @param canGetUsers - Whether the current user has permission to get user details
 */
export function getDriverInfo(
    api: Api,
    driverId: string | undefined,
    canGetUsers: boolean
): Promise<IUser | IIdEntity | undefined> {
    return new Promise<IUser | IIdEntity | undefined>((resolve, reject) => {
        if (canGetUsers && driverId && driverId !== "UnknownDriverId") {
            api.call(
                "Get",
                {
                    typeName: "User",
                    search: { id: driverId },
                    resultsLimit: 1,
                    propertySelector: {
                        fields: ["id", "name", "firstName", "lastName"],
                        isIncluded: true
                    }
                },
                users => {
                    resolve(users?.length ? users[0] : { id: driverId });
                },
                reject
            );
        } else {
            resolve(driverId ? { id: driverId } : undefined);
        }
    });
}

/**
 * Get the last driver assigned to a device
 * Fires DriverChange and DeviceStatusInfo calls in parallel, preferring DriverChange when it has driver data
 */
export async function getLastDriverForDevice(
    api: Api,
    deviceId: string,
    canGetUsers: boolean
): Promise<IUser | IIdEntity | undefined> {
    const [driverChange, deviceStatusInfoDriver] = await Promise.all([
        getLatestDriverChange(api, deviceId).catch(() => null),
        getDriverFromDeviceStatusInfo(api, deviceId).catch(() => undefined)
    ]);

    // Prefer DriverChange; fall back to DeviceStatusInfo
    const driver = driverChange?.driver ?? deviceStatusInfoDriver;
    if (!driver) {
        return undefined;
    }

    const driverId = typeof driver === "string" ? driver : driver.id;
    return getDriverInfo(api, driverId, canGetUsers).catch(() => undefined);
}


/**
 * Check if a driver is different from the current user and needs confirmation
 */
export function needsDriverChangeConfirmation(
    lastDriver: IUser | IIdEntity | undefined,
    currentUserId: string | undefined
): boolean {
    if (!lastDriver || !currentUserId) {
        return false;
    }
    const lastDriverId = lastDriver.id;
    return lastDriverId !== "UnknownDriverId" && lastDriverId !== currentUserId;
}

/**
 * Interface for driver change confirmation data
 */
export interface IDriverChangeConfirmation {
    deviceName: string;
    currentDriverName: string | undefined;
}

/**
 * Prepare data for driver change confirmation dialog
 */
export function prepareDriverChangeConfirmation(
    device: { id: string; name?: string },
    lastDriver: IUser | IIdEntity | undefined
): IDriverChangeConfirmation {
    return {
        deviceName: device.name || device.id,
        currentDriverName: (lastDriver as IUser)?.name || undefined
    };
}

/**
 * Interface for co-driver access check result
 */
export interface ICoDriverAccessResult {
    coDriversWithoutAccess: string[];
    coDriversLostSession: string[];
}

/**
 * Interface for co-driver entity (full user object with name)
 */
export interface ICoDriver {
    name: string;
    id: string;
}

/**
 * Check which co-drivers have valid credentials (sessions)
 * Returns co-drivers that have lost their session
 * @param coDrivers - List of co-drivers to check
 * @param api - API instance to get credentials
 * @param appManager - AppManager to get cached context
 */
export function getCoDriversWithLostSessions(
    coDrivers: ICoDriver[],
    api: Api,
    appManager: IAppManagerWithContext
): { withValidSession: { name: string; context: IContextWithApi }[]; lostSession: string[] } {
    const lostSession: string[] = [];
    const withValidSession: { name: string; context: IContextWithApi }[] = [];

    for (const coDriver of coDrivers) {
        const credentials = api.getDriverCredentials(coDriver.name);
        if (!credentials) {
            lostSession.push(coDriver.name);
        } else {
            const coDriverContext = appManager.getCachedContext(credentials.credentials.userName);
            if (coDriverContext) {
                withValidSession.push({ name: coDriver.name, context: coDriverContext });
            } else {
                lostSession.push(coDriver.name);
            }
        }
    }

    return { withValidSession, lostSession };
}

/**
 * Check if co-drivers have access to the specified device
 * Returns names of co-drivers that don't have access
 */
export async function checkCoDriversDeviceAccess(
    coDriversWithContext: { name: string; context: IContextWithApi }[],
    deviceId: string
): Promise<string[]> {
    const results = await Promise.all(
        coDriversWithContext.map(({ name, context }) =>
            new Promise<{ name: string; hasAccess: boolean }>(resolve => {
                context.api.call(
                    "Get",
                    {
                        typeName: "Device",
                        search: { id: deviceId },
                        propertySelector: {
                            fields: ["id"],
                            isIncluded: true
                        }
                    },
                    (deviceResults: IDevice[]) => {
                        resolve({ name, hasAccess: deviceResults !== null && deviceResults.length > 0 });
                    },
                    () => {
                        resolve({ name, hasAccess: false });
                    }
                );
            }))
    );

    return results
        .filter(({ hasAccess }) => !hasAccess)
        .map(({ name }) => name);
}

/**
 * Full co-driver access check for a device
 * Checks both session validity and device access
 */
export async function getCoDriversAccessToDevice(
    deviceId: string,
    coDrivers: ICoDriver[],
    api: Api,
    appManager: IAppManagerWithContext
): Promise<ICoDriverAccessResult> {
    if (coDrivers.length === 0) {
        return { coDriversWithoutAccess: [], coDriversLostSession: [] };
    }

    // Check which co-drivers have valid credentials (sessions)
    const { withValidSession, lostSession } = getCoDriversWithLostSessions(coDrivers, api, appManager);

    // If there are co-drivers with lost sessions, return them first
    if (lostSession.length > 0) {
        return { coDriversWithoutAccess: [], coDriversLostSession: lostSession };
    }

    // Check if co-drivers with valid sessions have access to the device
    const coDriversWithoutAccess = await checkCoDriversDeviceAccess(withValidSession, deviceId);

    return { coDriversWithoutAccess, coDriversLostSession: [] };
}

/**
 * Handle vehicle change when a driver has an exemption status (PC or YM)
 * If any active user has a PC/YM status on a different device, or the driver
 * is currently on no vehicle (e.g. after re-login or "Continue without vehicle"),
 * trigger an event to end the exemption
 * @param newDeviceId - The new device ID being selected
 * @param activeUsers - List of active users to check
 * @param getLatestStatusLog - Function to get the latest status log for a driver
 * @param serverTime - Server time for the event
 * @returns Whether the event was dispatched
 */
export function handleVehicleChangeWithExemptionLog(
    newDeviceId: string,
    activeUsers: IIdEntity[],
    getLatestStatusLog: (driver: IIdEntity) => DutyStatusLog | null | undefined,
    serverTime: Date,
    oldVehicleId: string | null | undefined
): boolean {
    const fromNoVehicle = (!oldVehicleId || oldVehicleId === "NoDeviceId") && !window.DRIVE_DEBUG?.KEEP_EXEMPTION_ON_LOGIN;
    let isDispatched = false;

    for (const user of activeUsers) {
        const latestStatusLog = getLatestStatusLog(user);
        if (latestStatusLog &&
            (latestStatusLog.device.id !== newDeviceId || fromNoVehicle) &&
            latestStatusLog.isPcYm()) {
            dispatcher.trigger(
                DispatcherEvents.VehicleChangedInExemption,
                latestStatusLog,
                { id: newDeviceId },
                serverTime
            );
            isDispatched = true;
        }
    }

    return isDispatched;
}

/**
 * Options for performing vehicle selection
 */
export interface IPerformVehicleSelectionOptions {
    vehicleService: VehicleService;
    dutyStatusLogService: DutyStatusLogService;
    userService: UserService;
    deviceCache: BaseCache<Device | IDevice>;
    store: DriveStore;
}

/**
 * Options for performing "no vehicle" selection
 */
export interface IPerformNoVehicleSelectionOptions {
    vehicleService: VehicleService;
    dutyStatusLogService: DutyStatusLogService;
    userService: UserService;
    deviceCache: BaseCache<Device | IDevice>;
    store: DriveStore;
}

/**
 * Perform "no vehicle" selection
 * Handles co-driver check, unassign driver, exemption log, and service call for continuing without a vehicle
 * @returns Promise that resolves on success
 */
export async function performNoVehicleSelection(
    userName: string,
    options: IPerformNoVehicleSelectionOptions
): Promise<void> {
    const { vehicleService, dutyStatusLogService, userService, deviceCache, store } = options;
    const user = userService.getCurrentUser();
    const noVehicleDevice = { id: "NoDeviceId", name: "" };

    // Dispatch OpenCab workflow action
    store.dispatch(OpenCabWorkflowActions.setSkippedAssetSelection(false));

    // Get old vehicle and current driver
    const oldVehicle = getCurrentVehicle();
    const currentDriver = userService.getDriversSeatUser();
    const serverTime = getServerDate();

    // If this user is not the driver's seat user, unassign previous driver from old vehicle
    let coDriver: IIdEntity | null = null;
    if (currentDriver && user?.id && currentDriver.id && currentDriver.id !== user.id) {
        coDriver = { id: currentDriver.id };
    }

    // Unassign the current driver from the old vehicle (or assigned vehicle)
    if (oldVehicle || coDriver) {
        await vehicleService.setDriverVehicle(oldVehicle, "UnknownDriverId", true, coDriver, serverTime);
    }

    // Handle exemption log for PC/YM status changes
    const activeUsers = userService.getUsers(true);
    const getLatestStatusLog = (driver: IIdEntity) => {
        const driverLogs = dutyStatusLogService.getHOSLogs(true, driver.id)
            .filter(hosLog => hosLog.isStandardStatusDriverLog());
        return dutyStatusLogService.getLatestLog(driverLogs);
    };
    handleVehicleChangeWithExemptionLog(noVehicleDevice.id, activeUsers, getLatestStatusLog, serverTime, oldVehicle?.id);

    // Add no-device to cache
    deviceCache.add([noVehicleDevice]);

    // Set no vehicle using selectVehicle
    // This sets currentVehicle, creates login/logoff logs, triggers events, and syncs
    await vehicleService.selectVehicle(userName, noVehicleDevice);
}

/**
 * Perform the actual vehicle selection
 * Handles cache, dispatch, unassign previous driver, exemption log, and service call
 * @returns Promise that resolves on success
 */
export async function performVehicleSelectionFlow(
    device: Device,
    userName: string,
    options: IPerformVehicleSelectionOptions
): Promise<void> {
    const { vehicleService, dutyStatusLogService, userService, deviceCache, store } = options;
    const user = userService.getCurrentUser();

    // Add device to cache first (required by selectVehicle's getOrFetch)
    deviceCache.add(device.toJSON());

    // Dispatch OpenCab workflow action
    store.dispatch(OpenCabWorkflowActions.setSkippedAssetSelection(false));

    // If this user is not the driver's seat user, unassign previous driver from old vehicle first
    const oldVehicle = getCurrentVehicle();
    const currentDriver = userService.getDriversSeatUser();

    if (oldVehicle && !isNoVehicle() && currentDriver && user?.id && currentDriver.id && currentDriver.id !== user.id) {
        const coDriver = { id: currentDriver.id };
        await vehicleService.setDriverVehicle(oldVehicle, "UnknownDriverId", true, coDriver);
    }

    // Handle exemption log for PC/YM status changes
    const serverTime = getServerDate();
    const activeUsers = userService.getUsers(true);

    const getLatestStatusLog = (driver: IIdEntity) => {
        const driverLogs = dutyStatusLogService.getHOSLogs(true, driver.id)
            .filter(hosLog => hosLog.isStandardStatusDriverLog());
        return dutyStatusLogService.getLatestLog(driverLogs);
    };

    handleVehicleChangeWithExemptionLog(device.id, activeUsers, getLatestStatusLog, serverTime, oldVehicle?.id);

    // Perform the actual selection
    await vehicleService.selectVehicle(userName, device);
}

/**
 * Result of checking vehicle selection prerequisites
 */
export interface IVehicleSelectionCheckResult {
    canProceed: boolean;
    needsDriverConfirmation: boolean;
    driverConfirmationData?: IDriverChangeConfirmation;
    coDriversLostSession: string[];
    coDriversWithoutAccess: string[];
}

/**
 * Check all prerequisites before vehicle selection
 * Returns what actions (if any) are needed before selection can proceed
 */
export async function checkVehicleSelectionPrerequisites(
    deviceId: string,
    currentUserId: string,
    canGetUsers: boolean,
    isIoxAttached: boolean,
    coDrivers: ICoDriver[],
    api: Api,
    appManager: IAppManagerWithContext,
    device: { id: string; name?: string }
): Promise<IVehicleSelectionCheckResult> {
    // Check co-driver access to the device
    if (coDrivers.length > 0) {
        const accessResult = await getCoDriversAccessToDevice(deviceId, coDrivers, api, appManager);

        if (accessResult.coDriversLostSession.length > 0) {
            return {
                canProceed: false,
                needsDriverConfirmation: false,
                coDriversLostSession: accessResult.coDriversLostSession,
                coDriversWithoutAccess: []
            };
        }

        if (accessResult.coDriversWithoutAccess.length > 0) {
            return {
                canProceed: false,
                needsDriverConfirmation: false,
                coDriversLostSession: [],
                coDriversWithoutAccess: accessResult.coDriversWithoutAccess
            };
        }
    }

    // Check if another driver is assigned to this vehicle
    const lastDriver = await getLastDriverForDevice(api, deviceId, canGetUsers);

    // Skip confirmation if IOX-USB is attached (auto-assignment) or if no confirmation needed
    if (needsDriverChangeConfirmation(lastDriver, currentUserId) && !isIoxAttached) {
        return {
            canProceed: false,
            needsDriverConfirmation: true,
            driverConfirmationData: prepareDriverChangeConfirmation(device, lastDriver),
            coDriversLostSession: [],
            coDriversWithoutAccess: []
        };
    }

    return {
        canProceed: true,
        needsDriverConfirmation: false,
        coDriversLostSession: [],
        coDriversWithoutAccess: []
    };
}
